import React from 'react';
import PropTypes from 'prop-types';
import Input from '@upsales/components/Input';
import Button from '@upsales/components/Button';
import bemClass from '@upsales/components/Utils/bemClass';
import { LeadStatus, TableIndicator, Toggle, Loader, ButtonSelect, Tooltip, Icon } from '@upsales/components';
import AccountListWrapper from '../AccountListWrapper/AccountListWrapper';
import List from '../AccountList/AccountList';
import { leadStatus as leadStatusTooltips } from '../../Helpers/TooltipHelper';

import Name from './Columns/Name';
import Phone from './Columns/Phone';
import Email from './Columns/Email';
import Controls from './Columns/Controls';
import SubaccountsColumn from './Columns/SubaccountsColumn';

import './AccountListContacts.scss';
import _ from 'lodash';
import T from 'Components/Helpers/translate';
import RelationsSignalWidget from 'App/components/RelationsSignalWidget/RelationsSignalWidget';
import ContactPriorityIcon from 'App/components/columns/ContactPriorityIcon';
import { isAvailable, Feature } from 'App/services/FeatureHelperStandalone';

// The labels the contact columns read. Moved here from LegacyReactTemplates/listContacts.jsx
// (contactRowStatics), whose only other content was the dead contact rows (WEB-13559).
const getStatics = () => ({
	editTool: T('default.edit'),
	deleteTool: T('default.delete'),
	unsubscribed: T('default.unsubscribedFromGroupMail'),
	noCampaigns: T('default.noCampaigns'),
	noCategories: T('default.noCategories'),
	noSegments: T('default.noSegments'),
	exclude: T('default.exclude'),
	excluded: T('bannerEditor.excluded'),
	include: T('segment.include'),
	contact: T('tag.contact'),
	unsubscribedFrom: T('default.unsubscribedFrom')
});

const propTypes = {
	actions: PropTypes.object.isRequired,
	accountContacts: PropTypes.array.isRequired,
	searchByRelevance: PropTypes.bool,
	showInactiveContacts: PropTypes.bool,
	accountId: PropTypes.number.isRequired,
	tableLoading: PropTypes.bool,
	account: PropTypes.object,
	subAccountIds: PropTypes.array,
	sortColumn: PropTypes.string,
	sortAsc: PropTypes.bool
};

class AccountListContacts extends React.Component {
	constructor(props) {
		super(props);

		const statics = getStatics();
		const { editContact, removeContact } = this.props.actions;
		const subAccountIds = this.props.subAccountIds;
		const tooltips = {
			market: contact => ['visit', 'mail', 'form'].map(type => T(leadStatusTooltips(type, contact))),
			sales: contact => ['activity', 'opportunity', 'order'].map(type => T(leadStatusTooltips(type, contact)))
		};

		this.columns = [
			{
				key: 'priority',
				title: '',
				render: item => <ContactPriorityIcon key={'priority-' + item.id} contact={item} />,
				grow: 0,
				width: 32
			},
			{
				key: 'name',
				title: T('default.name'),
				sortable: true,
				render: item => (
					<Name key={'name-' + item.id} contact={item} client={item.client} accountId={props.accountId} />
				),
				grow: 4,
				width: 0
			},
			{
				key: 'actualphone',
				title: T('default.phone'),
				sortable: true,
				render: item => <Phone key={'actualphone-' + item.id} contact={item} />,
				grow: 2,
				width: 0
			},
			{
				key: 'cellphone',
				title: T('default.cellPhone'),
				sortable: true,
				render: item => <Phone key={'cellphone-' + item.id} contact={item} cellphone />,
				grow: 2,
				width: 0
			}
		];

		if (subAccountIds?.length > 0) {
			this.columns.splice(1, 0, {
				key: 'subaccount',
				title: T('account.subaccount'),
				render: item =>
					item.client?.operationalAccount?.id ? <SubaccountsColumn client={item.client} /> : null,
				grow: 2,
				width: 0
			});
		}

		if (isAvailable(Feature.VISITS) && isAvailable(Feature.EMAIL) && isAvailable(Feature.FORMS)) {
			this.columns.push({
				key: 'markethistory',
				title: T('default.marketActivity'),
				render: item => (
					<LeadStatus
						key={'markethistory-' + item.id + (item.searchByRelevance ? '-searchByRelevance' : '-alpha')}
						className="Column"
						contact={item}
						market
						size={'small'}
						tooltips={tooltips.market(item)}
					/>
				),
				grow: 2,
				width: 0,
				hideOnWidth: 1366
			});
		}

		this.columns.push({
			key: 'score',
			title: T('default.score'),
			sortable: true,
			render: item => <div key={'score-' + item.id} className="Column">{`${item.score || 0}p`}</div>,
			grow: 1,
			width: 0,
			hideOnWidth: 1199
		});
		this.columns.push({
			key: 'history',
			title: T('default.history'),
			render: item => (
				<LeadStatus
					key={'saleshistory-' + item.id + (item.searchByRelevance ? '-searchByRelevance' : '-alpha')}
					contact={item}
					size={'small'}
					tooltips={tooltips.sales(item)}
				/>
			),
			grow: 2,
			width: 0,
			hideOnWidth: 1022
		});
		this.columns.push({
			key: 'email',
			title: T('default.email'),
			sortable: true,
			render: item => <Email key={'email-' + item.id} contact={item} statics={statics} />,
			grow: 2,
			width: 100
		});

		if (isAvailable(Feature.AI)) {
			this.columns.push({
				key: 'signals',
				title: T('default.signals'),
				render: item => (
					<RelationsSignalWidget
						barOnly
						clientId={item.client.id}
						contactId={item.id}
						signals={item.threadSignals}
					/>
				),
				grow: 2,
				width: 0
			});
		}

		this.columns.push({
			key: 'controls',
			title: '',
			render: item => (
				<Controls
					key={'controls' + item.id}
					contact={item}
					statics={statics}
					entity={'default.contact'}
					removeEntity={() => removeContact(item)}
					editEntity={() => editContact(item)}
				/>
			),
			grow: 0,
			width: 64,
			marginRight: '0px'
		});

		this.lang = {
			contacts: T('default.contacts'),
			addContact: T('default.addContact'),
			activeContacts: T('default.activeContacts'),
			searchContact: T('default.searchContact'),
			relevance: T('default.relevance'),
			aToZ: T('default.aToZ'),
			showInactiveContacts: T('default.showInactiveContacts'),
			showBoardMembers: T('prospecting.showBoardMembers')
		};

		this.charArray = _.map(T('CHARARRAY'), c => c);
		this.doOnScroll = true;

		this.state = {
			rows: [],
			numActiveContacts: 0,
			indicatorIndex: 0,
			showDirectorsButton: true,
			toggleTooltipActive: false
		};

		this.onScroll = this.onScroll.bind(this);
		this.renderRow = this.renderRow.bind(this);
		this.scrollToRow = this.scrollToRow.bind(this);
		this.getRowHeight = this.getRowHeight.bind(this);
		this.calcScollHeight = _.throttle(this.calcScollHeight.bind(this), 500);
		this.renderTableHeader = this.renderTableHeader.bind(this);
		this.classes = new bemClass('AccountListContacts');
	}
	onResize = _.debounce(() => {
		const controlsWrapper = document.getElementsByClassName(this.classes.elem('control').b())[0];
		const wrapperHeader = document.getElementsByClassName('wrapper-header')[0];

		if (wrapperHeader?.getBoundingClientRect?.().right > controlsWrapper?.getBoundingClientRect?.().left + 25) {
			if (!this.state.toggleTooltipActive) {
				this.setState({ toggleTooltipActive: true });
			}
		} else {
			if (this.state.toggleTooltipActive) {
				this.setState({ toggleTooltipActive: false });
			}
		}
	}, 100);

	componentDidMount() {
		window.addEventListener('resize', this.onResize);
		this.onResize();
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.onResize);
	}

	// eslint-disable-next-line camelcase
	UNSAFE_componentWillReceiveProps(newProps) {
		if (newProps.accountContacts.length !== this.props.accountContacts.length) {
			const numActiveContacts = _.filter(newProps.accountContacts, c => c.active).length;
			this.setState({ numActiveContacts });
		}
	}

	onScroll({ scrollTop }) {
		const listWrapper = document.getElementsByClassName('AccountListBoardOfDirectors')[0];
		if (listWrapper && listWrapper.offsetTop < scrollTop + 80) {
			this.setState({ showDirectorsButton: false });
		} else if (listWrapper && !this.state.showDirectorsButton && listWrapper.offsetTop >= scrollTop + 80) {
			this.setState({ showDirectorsButton: true });
		}
		const { searchByRelevance, accountContacts } = this.props;
		if (!searchByRelevance && this.doOnScroll) {
			if (scrollTop === 0) {
				this.setState({
					indicatorIndex: 0
				});
			} else {
				const index = this.calcScollHeight(accountContacts, scrollTop);
				if (index !== null && accountContacts.length) {
					const indicatorIndex = _.findIndex(
						this.charArray,
						c => c.toLowerCase() === (accountContacts[index].name[0] || '').toLowerCase()
					);
					this.setState({
						indicatorIndex: indicatorIndex >= 0 ? indicatorIndex : 0
					});
				}
			}
		}
	}

	calcScollHeight(accountContacts, scrollTop) {
		let calcHeight = 0;
		let index = null;
		for (index = 0; index < accountContacts.length - 1; index++) {
			calcHeight += this.getRowHeight({ index });
			if (calcHeight >= scrollTop) {
				break;
			}
		}
		return index;
	}

	findClosestRow(char) {
		const { accountContacts } = this.props;
		let scrollTop = 0;
		for (var i = 0; i < accountContacts.length; i++) {
			if (accountContacts[i].delimiter && accountContacts[i].name.toLowerCase() === char.toLowerCase()) {
				break;
			} else if (i === accountContacts.length - 1) {
				// Char not found
				const index = _.findIndex(this.charArray, c => c === char);
				if (index > 0) {
					// Search char before in char array
					const charBefore = this.charArray[index - 1];
					return this.findClosestRow(charBefore);
				} else {
					// Return first
					return 0;
				}
			}
			scrollTop += this.getRowHeight({ index: i });
		}
		return scrollTop;
	}

	scrollToRow(char, indicatorIndex) {
		this.doOnScroll = false;

		const scrollTop = this.findClosestRow(char);
		window.scrollTo({ top: scrollTop });

		// Execute after onScroll has been triggered
		setTimeout(() => {
			this.setState({ indicatorIndex });
			this.doOnScroll = true;
			if (scrollTop !== document.documentElement.scrollTop) {
				window.scrollTo({ top: scrollTop });
			}
		}, 100);
	}

	getRowHeight({ index }) {
		const item = this.props.accountContacts[index];
		return item.delimiter ? 20 : 40;
	}
	scrollToListBoadOfDirectors() {
		const element = document.getElementsByClassName('AccountListBoardOfDirectors')[0];
		const yOffset = -150;
		const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

		window.scrollTo({ top: y, behavior: 'smooth', block: 'start' });
	}
	renderRow({ index, key, style }) {
		const item = this.props.accountContacts[index];
		const searchByRelevance = this.props.searchByRelevance;
		const openContact = this.props.actions.openContact;
		const rowClassNames = [this.classes.elem('List-row').b()];
		if (item.active === 0) {
			rowClassNames.push(this.classes.elem('List-row-inactive').b());
		}
		item.searchByRelevance = searchByRelevance;
		return item.delimiter ? (
			<div className={this.classes.elem('List-delimiter-row').b()} key={key} style={style}>
				{item.name}
			</div>
		) : (
			<div
				id={item.id}
				className={rowClassNames.join(' ')}
				key={key}
				style={style}
				onClick={() => openContact(item.id)}
			>
				{this.columns.map((col, i) =>
					window.innerWidth < col.hideOnWidth ? null : (
						<div
							key={'contact-row-' + i}
							style={{ flex: `${col.grow} 1 ${col.width}px`, marginRight: col.marginRight || '10px' }}
						>
							{col.render(item)}
						</div>
					)
				)}
			</div>
		);
	}

	renderTableHeader() {
		const { sortColumn, sortAsc, searchByRelevance, actions } = this.props;
		return (
			<div className={this.classes.elem('TableHeader').b()}>
				{this.columns.map((c, i) => {
					const isActive = !searchByRelevance && c.sortable && sortColumn === c.key;
					const isSortable = c.sortable && !searchByRelevance;
					const className = [
						this.classes.elem('TableHeader-col').b(),
						isSortable ? this.classes.elem('TableHeader-col--sortable').b() : '',
						isActive ? this.classes.elem('TableHeader-col--active').b() : ''
					]
						.filter(Boolean)
						.join(' ');
					return (
						<div
							key={'header-' + i}
							className={className}
							style={{ flex: `${c.grow} 1 ${c.width}px`, marginRight: c.marginRight || '10px' }}
							onClick={isSortable ? () => actions.onSort(c.key) : undefined}
						>
							{c.title}
							{isActive ? (
								<Icon
									name={sortAsc ? 'chevron-up' : 'chevron-down'}
									className={this.classes.elem('TableHeader-sort-icon').b()}
								/>
							) : null}
						</div>
					);
				})}
			</div>
		);
	}

	render() {
		const {
			account,
			searchByRelevance,
			showInactiveContacts,
			tableLoading,
			actions: { searchChanged, toogleSearchBy, toogleshowInactiveContacts }
		} = this.props;
		const { numActiveContacts, indicatorIndex } = this.state;
		const hasProOrAdanced = isAvailable(Feature.PROSPECTING_PRO);

		const hasDirectors =
			account && account.prospecting && account.prospecting.directors && account.prospecting.directors.length > 0;

		return (
			<div className={this.classes.b()}>
				<AccountListWrapper
					title={this.lang.contacts}
					subtitle={`${numActiveContacts} ${this.lang.activeContacts}`}
					subtitleElement={
						hasProOrAdanced && this.state.showDirectorsButton && hasDirectors ? (
							<Button
								className={this.classes.elem('showDirectorsButton').b()}
								type="link"
								onClick={this.scrollToListBoadOfDirectors}
							>
								{this.lang.showBoardMembers}
							</Button>
						) : null
					}
				>
					<div className={this.classes.elem('control').b()}>
						<div className="only-active-toggle-container">
							<span
								className={`only-active-toggle-container-label${
									this.state.toggleTooltipActive ? '--hide' : ''
								}`}
							>
								{this.lang.showInactiveContacts}
							</span>
							<Tooltip disabled={!this.state.toggleTooltipActive} title={this.lang.showInactiveContacts}>
								<Toggle
									icon={this.state.toggleTooltipActive ? 'user-slash' : ''}
									checked={showInactiveContacts}
									onChange={value => toogleshowInactiveContacts(value)}
								/>
							</Tooltip>
						</div>
						<div className="search-container">
							<Input
								icon="search"
								autoFocus
								onChange={e => searchChanged(e.target.value)}
								placeholder={this.lang.searchContact}
								onFocus={this.onResize}
								onBlur={this.onResize}
							/>
						</div>
						<ButtonSelect
							disabled={tableLoading}
							options={[
								{ value: 'relevance', title: this.lang.relevance },
								{ value: 'aToZ', title: this.lang.aToZ }
							]}
							value={searchByRelevance ? 'relevance' : 'aToZ'}
							onChange={v => toogleSearchBy(v === 'relevance')}
						/>
					</div>
					{searchByRelevance || this.props.sortColumn !== 'name' || !this.state.showDirectorsButton ? null : (
						<div className={'TableIndicator'}>
							<TableIndicator
								array={this.charArray}
								index={indicatorIndex}
								color="grey"
								onClick={this.scrollToRow}
							/>
						</div>
					)}
					{this.renderTableHeader()}
				</AccountListWrapper>
				{tableLoading ? (
					<div>
						<Loader size="sm" className={this.classes.elem('loader').b()} />
					</div>
				) : (
					<List
						key={`contact-list-${searchByRelevance ? 'relevance' : 'alpha'}`}
						size={this.props.accountContacts.length}
						marginTop={searchByRelevance ? 86 : 104}
						getRowHeight={this.getRowHeight}
						rowRenderer={this.renderRow}
						onScroll={this.onScroll}
						// Used to force update list
						forceUpdate={searchByRelevance}
					/>
				)}
			</div>
		);
	}
}

AccountListContacts.propTypes = propTypes;
export default AccountListContacts;
